import { PropertyDisplayRule } from "./DynamicProperties";

/**
 * Helper function to format explanation text for tooltips
 */
export const formatExplanation = (explanation: any): string => {
  if (!explanation) return "No explanation available";
  if (Array.isArray(explanation)) return explanation.join("\n");
  if (typeof explanation === "string") return explanation;
  return String(explanation);
};

/**
 * Hidden fields for Issue entities
 */
export const hiddenIssueFields = [
  // Core fields shown in header/summary
  "uuid",
  "title",
  "description",
  "issueKey",
  "issueType",
  "priority",
  "status",
  "summary",
  "epic.title",
  "entityUuid",
  "_postEnriched",
  "_postProcessed",
  "deleted",
  "mentions",

  // Explanation fields shown in insights
  "vzblClarityExplanation",
  "vzblFrictionExplanation",
  "vzblUrgencyExplanation",
  "vzblImpactExplanation",
  "vzblClassificationExplanation",
  "vzblValueStatement",
  "vzblMissingDetails",
  "vzblUnansweredQuestions",
  "vzblSummary",

  // Duplicative fields
  "assignee",
  "reporter",
  "currentsprint",
  "assignees",
  "assignees.team",
  "currentReporter",
  "currentAssigneesTeam",

  // Metrics properties
  "vzblCycleTime",
  "vzblLeadTime",

  // Internal metadata
  "integration",
  "sourceUrl",

  // Duplicate/legacy fields
  "timespent", // use timeSpent instead
];

/**
 * Hidden fields for Pull Request entities
 */
export const hiddenPullRequestFields = [
  // Core fields shown in header/summary
  "uuid",
  "title",
  "description",
  "body",
  "number",
  "status",
  "entityUuid",

  // URLs and links (shown elsewhere or internal)
  "sourceUrl",
  "diffUrl",
  "issueUrl",
  "reviewCommentUrl",

  // Internal metadata
  "_postEnriched",
  "_postProcessed",
  "deleted",
  "mentions",
  "integration",

  // Explanation fields shown in insights
  "complexityExplanation",
  "maintainabilityExplanation",
  "qualityExplanation",
  "riskExplanation",
  "summaryChangeset",
  "summaryPurpose",
  "unansweredQuestions",
];

/**
 * Display rules for Issue entities
 */
export const issueDisplayRules: Record<string, PropertyDisplayRule> = {
  // People
  CurrentAssignee: {
    priority: 900,
    category: "People",
    displayName: "Assigned To",
    showIfEmpty: true,
    valueType: "person",
  },
  "CurrentAssignee.Team": {
    priority: 600,
    category: "People",
    displayName: "Assignee Teams",
    showIfEmpty: false,
    valueType: "tag",
  },
  CreatedBy: {
    priority: 800,
    category: "People",
    displayName: "Created By",
    showIfEmpty: true,
    valueType: "person",
  },
  CurrentReporter: {
    priority: 500,
    category: "People",
    displayName: "Reporter",
    showIfEmpty: true,
    valueType: "person",
  },
  Assignees: {
    priority: 499,
    category: "People",
    displayName: "All Assignees",
    showIfEmpty: false,
    valueType: "person",
  },
  "Assignees.Team": {
    priority: 502,
    category: "People",
    displayName: "All Teams",
    showIfEmpty: false,
    valueType: "tag",
  },

  // Sprint and Epic
  CurrentSprints: {
    priority: 600,
    category: "Workflow",
    displayName: "Current Sprints",
    showIfEmpty: false,
    valueType: "tag",
  },
  Sprints: {
    priority: 600,
    category: "Workflow",
    displayName: "All Sprints",
    showIfEmpty: false,
    valueType: "tag",
  },
  Epic: {
    priority: 730,
    category: "Workflow",
    displayName: "Epic",
    showIfEmpty: false,
    valueType: "link",
  },
  "Epic.title": {
    priority: 720,
    category: "Workflow",
    displayName: "Epic Title",
    showIfEmpty: false,
    valueType: "text",
  },

  // Temporal
  createdAt: {
    priority: 900,
    category: "Workflow",
    displayName: "Created Date",
    showIfEmpty: false,
    valueType: "date",
  },
  updatedAt: {
    priority: 600,
    category: "Workflow",
    displayName: "Last Updated",
    showIfEmpty: false,
    valueType: "date",
  },
  startedAt: {
    priority: 700,
    category: "Workflow",
    displayName: "Started Date",
    showIfEmpty: false,
    valueType: "date",
  },
  completedAt: {
    priority: 870,
    category: "Workflow",
    displayName: "Completed Date",
    showIfEmpty: false,
    valueType: "date",
  },
  closedAt: {
    priority: 801,
    category: "Workflow",
    displayName: "Closed Date",
    showIfEmpty: false,
    valueType: "date",
  },
  Age: {
    priority: 605,
    category: "Workflow",
    displayName: "Age",
    showIfEmpty: false,
    valueType: "duration",
  },
  ResolutionTime: {
    priority: 840,
    category: "Workflow",
    displayName: "Resolution Time",
    showIfEmpty: false,
    valueType: "duration",
  },

  // Metrics
  storyPoints: {
    priority: 700,
    category: "Effort",
    displayName: "Story Points",
    showIfEmpty: false,
    valueType: "number",
  },
  timeSpent: {
    priority: 690,
    category: "Effort",
    displayName: "Time Spent",
    showIfEmpty: false,
    valueType: "duration",
  },
  originalEstimate: {
    priority: 680,
    category: "Effort",
    displayName: "Original Estimate",
    showIfEmpty: false,
    valueType: "duration",
  },
  remainingEstimate: {
    priority: 670,
    category: "Effort",
    displayName: "Remaining Estimate",
    showIfEmpty: false,
    valueType: "duration",
  },

  // Vzbl metrics
  vzblClockCycleTime: {
    priority: 400,
    category: "Process",
    displayName: "Clock Cycle Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  vzblClockLeadTime: {
    priority: 300,
    category: "Process",
    displayName: "Clock Lead Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  vzblCycleTime: {
    priority: 300,
    category: "Process",
    displayName: "Cycle Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  vzblLeadTime: {
    priority: 570,
    category: "Process",
    displayName: "Lead Time",
    showIfEmpty: false,
    valueType: "duration",
  },

  // Classification and Analysis
  vzblClassification: {
    priority: 750,
    category: "Analysis",
    displayName: "Classification",
    showIfEmpty: false,
    valueType: "classification",
    tooltip: (props: Record<string, any>) =>
      formatExplanation(props.vzblClassificationExplanation),
  },
  vzblUrgency: {
    priority: 740,
    category: "Analysis",
    displayName: "Urgency",
    showIfEmpty: false,
    valueType: "urgency",
    tooltip: (props: Record<string, any>) =>
      formatExplanation(props.vzblUrgencyExplanation),
  },
  vzblImpact: {
    priority: 730,
    category: "Analysis",
    displayName: "Impact",
    showIfEmpty: false,
    valueType: "status",
    tooltip: (props: Record<string, any>) =>
      formatExplanation(props.vzblImpactExplanation),
  },
  vzblClarityScore: {
    priority: 720,
    category: "Analysis",
    displayName: "Clarity",
    showIfEmpty: false,
    valueType: "score",
    tooltip: (props: Record<string, any>) =>
      formatExplanation(props.vzblClarityExplanation),
  },
  vzblFrictionScore: {
    priority: 710,
    category: "Analysis",
    displayName: "Complexity",
    showIfEmpty: false,
    valueType: "score",
    tooltip: (props: Record<string, any>) =>
      formatExplanation(props.vzblFrictionExplanation),
  },

  // Tags and Labels
  labels: {
    priority: 480,
    category: "Metadata",
    displayName: "Labels",
    showIfEmpty: false,
    valueType: "tag",
  },
  vzblKeywords: {
    priority: 470,
    category: "Metadata",
    displayName: "Keywords",
    showIfEmpty: false,
    valueType: "tag",
  },
  vzblTopics: {
    priority: 460,
    category: "Metadata",
    displayName: "Topics",
    showIfEmpty: false,
    valueType: "tag",
  },
};

/**
 * Display rules for Pull Request entities
 */
export const pullRequestDisplayRules: Record<string, PropertyDisplayRule> = {
  // People
  createdBy: {
    priority: 1000,
    category: "People",
    displayName: "Author",
    showIfEmpty: false,
    valueType: "person",
  },
  reviewers: {
    priority: 700,
    category: "People",
    displayName: "Reviewers",
    showIfEmpty: false,
    valueType: "person",
  },

  // Source
  repository: {
    priority: 850,
    category: "Source",
    displayName: "Repository",
    showIfEmpty: false,
    valueType: "metadata",
  },
  branch: {
    priority: 851,
    category: "Source",
    displayName: "Branch",
    showIfEmpty: false,
    valueType: "metadata",
  },
  base: {
    priority: 805,
    category: "Source",
    displayName: "Base Branch",
    showIfEmpty: false,
    valueType: "metadata",
  },
  head: {
    priority: 805,
    category: "Source",
    displayName: "Head Branch",
    showIfEmpty: false,
    valueType: "metadata",
  },
  number: {
    priority: 800,
    category: "Source",
    displayName: "PR Number",
    showIfEmpty: false,
    valueType: "number",
  },

  // Status and Workflow
  merged: {
    priority: 400,
    category: "Workflow",
    displayName: "Merged",
    showIfEmpty: false,
    valueType: "boolean",
  },
  age: {
    priority: 600,
    category: "Workflow",
    displayName: "Age",
    showIfEmpty: false,
    valueType: "duration",
  },
  Age: {
    priority: 601,
    category: "Workflow",
    displayName: "Age (ms)",
    showIfEmpty: false,
    valueType: "duration",
  },
  createdAt: {
    priority: 880,
    category: "Workflow",
    displayName: "Created",
    showIfEmpty: false,
    valueType: "date",
  },
  mergedAt: {
    priority: 870,
    category: "Workflow",
    displayName: "Merged At",
    showIfEmpty: false,
    valueType: "date",
  },
  closedAt: {
    priority: 860,
    category: "Workflow",
    displayName: "Closed At",
    showIfEmpty: false,
    valueType: "date",
  },
  updatedAt: {
    priority: 800,
    category: "Workflow",
    displayName: "Last Updated",
    showIfEmpty: false,
    valueType: "date",
  },

  // Changes
  commits: {
    priority: 900,
    category: "Scope",
    displayName: "Commits",
    showIfEmpty: false,
    valueType: "number",
  },
  additions: {
    priority: 901,
    category: "Scope",
    displayName: "Additions",
    showIfEmpty: false,
    valueType: "number",
  },
  deletions: {
    priority: 902,
    category: "Scope",
    displayName: "Deletions",
    showIfEmpty: false,
    valueType: "number",
  },
  totalComments: {
    priority: 903,
    category: "Workflow",
    displayName: "Comments",
    showIfEmpty: false,
    valueType: "number",
  },

  // Process Metrics
  cycleTime: {
    priority: 700,
    category: "Process",
    displayName: "Cycle Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  pickupTime: {
    priority: 695,
    category: "Process",
    displayName: "Pickup Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  reviewTime: {
    priority: 690,
    category: "Process",
    displayName: "Review Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  mergeTime: {
    priority: 685,
    category: "Process",
    displayName: "Merge Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  commitLeadTime: {
    priority: 680,
    category: "Process",
    displayName: "Commit Lead Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  clockPickupTime: {
    priority: 675,
    category: "Process",
    displayName: "Clock Pickup Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  clockReviewTime: {
    priority: 670,
    category: "Process",
    displayName: "Clock Review Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  clockMergeTime: {
    priority: 665,
    category: "Process",
    displayName: "Clock Merge Time",
    showIfEmpty: false,
    valueType: "duration",
  },
  clockCommitLeadTime: {
    priority: 660,
    category: "Process",
    displayName: "Clock Commit Lead Time",
    showIfEmpty: false,
    valueType: "duration",
  },

  // Analysis
  vzblComplexity: {
    priority: 600,
    category: "Analysis",
    displayName: "Complexity",
    showIfEmpty: false,
    valueType: "score",
    tooltip: (props: Record<string, any>) =>
      formatExplanation(props.vzblComplexityExplanation),
  },
  vzblMaintainability: {
    priority: 590,
    category: "Analysis",
    displayName: "Maintainability",
    showIfEmpty: false,
    valueType: "score",
    tooltip: (props: Record<string, any>) =>
      formatExplanation(props.vzblMaintainabilityExplanation),
  },
  vzblQuality: {
    priority: 580,
    category: "Analysis",
    displayName: "Quality",
    showIfEmpty: false,
    valueType: "score",
    tooltip: (props: Record<string, any>) =>
      formatExplanation(props.vzblQualityExplanation),
  },
  vzblRiskScore: {
    priority: 570,
    category: "Analysis",
    displayName: "Risk Score",
    showIfEmpty: false,
    valueType: "score",
    tooltip: (props: Record<string, any>) =>
      formatExplanation(props.vzblRiskExplanation),
  },
  vzblConfidenceScore: {
    priority: 560,
    category: "Analysis",
    displayName: "Confidence",
    showIfEmpty: false,
    valueType: "score",
  },
  vzblHasOpenQuestions: {
    priority: 550,
    category: "Analysis",
    displayName: "Has Open Questions",
    showIfEmpty: false,
    valueType: "boolean",
  },
  vzblClassifications: {
    priority: 545,
    category: "Analysis",
    displayName: "Classifications",
    showIfEmpty: false,
    valueType: "tag",
  },

  // Metadata
  labels: {
    priority: 400,
    category: "Metadata",
    displayName: "Labels",
    showIfEmpty: false,
    valueType: "tag",
  },
  vzblKeywords: {
    priority: 390,
    category: "Metadata",
    displayName: "Keywords",
    showIfEmpty: false,
    valueType: "tag",
  },
  vzblTopics: {
    priority: 380,
    category: "Metadata",
    displayName: "Topics",
    showIfEmpty: false,
    valueType: "tag",
  },
};
